const IDLE_MS = 30_000;

/**
 * Tracks wall-clock time on a page and "active" time while the visitor scrolls, clicks, types,
 * or moves the mouse. A burst of activity stays active until IDLE_MS passes without interaction.
 */
export class ActiveTimeTracker {
  private readonly startedAt: number;
  private lastActivity = 0;
  private burstStart: number | null = null;
  /** Active time from bursts that have already closed (idle > IDLE_MS), banked with their grace. */
  private accumulatedActiveMs = 0;

  constructor(nowMs = Date.now()) {
    this.startedAt = nowMs;
  }

  markActive(nowMs = Date.now()): void {
    if (this.burstStart === null) {
      this.burstStart = nowMs;
    } else if (nowMs - this.lastActivity > IDLE_MS) {
      // The previous burst went idle past the timeout: bank it (with its 30s grace) and start a
      // new burst here, so a long mid-session idle gap is NOT counted as active time.
      this.accumulatedActiveMs += this.lastActivity + IDLE_MS - this.burstStart;
      this.burstStart = nowMs;
    }
    this.lastActivity = nowMs;
  }

  /** End timestamp for the current (open) active burst (extends until idle timeout). */
  private activeEnd(nowMs: number): number {
    if (this.burstStart === null) return this.lastActivity;
    if (nowMs - this.lastActivity <= IDLE_MS) return nowMs;
    return this.lastActivity + IDLE_MS;
  }

  snapshot(nowMs = Date.now()): { totalDwellSec: number; activeDwellSec: number } {
    let activeMs = this.accumulatedActiveMs;
    if (this.burstStart !== null) {
      activeMs += Math.max(0, this.activeEnd(nowMs) - this.burstStart);
    }
    return {
      totalDwellSec: Math.max(0, Math.round((nowMs - this.startedAt) / 1000)),
      activeDwellSec: Math.max(0, Math.round(activeMs / 1000)),
    };
  }
}

export function throttle(fn: () => void, ms: number): () => void {
  let last = 0;
  return () => {
    const now = Date.now();
    if (now - last >= ms) {
      last = now;
      fn();
    }
  };
}

/** Wire scroll/click/keyboard/touch/mouse listeners that feed an ActiveTimeTracker. */
export function attachActiveListeners(
  tracker: ActiveTimeTracker,
  target: Document | Window = document,
): () => void {
  const onActive = () => tracker.markActive();
  const onMove = throttle(() => tracker.markActive(), 1_000);
  const passive = { passive: true, capture: true } as AddEventListenerOptions;

  target.addEventListener("scroll", onActive, passive);
  target.addEventListener("click", onActive, passive);
  target.addEventListener("keydown", onActive, passive);
  target.addEventListener("touchstart", onActive, passive);
  window.addEventListener("mousemove", onMove, { passive: true });

  return () => {
    target.removeEventListener("scroll", onActive, passive);
    target.removeEventListener("click", onActive, passive);
    target.removeEventListener("keydown", onActive, passive);
    target.removeEventListener("touchstart", onActive, passive);
    window.removeEventListener("mousemove", onMove);
  };
}
